import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './SlashMenu.module.css';

// SlashMenu — popup driven by SlashCommand suggestion plugin. Position is
// snapshotted on each suggestion lifecycle event (open / query update) so
// internal re-renders (e.g. mouse hover changing the selected index) don't
// re-poll `clientRect`. That keeps the popup anchored while the user moves
// the pointer or scrolls.
export default function SlashMenu({ apiRef }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [index, setIndex] = useState(0);
  const commandRef = useRef(null);
  const indexRef = useRef(0);
  const itemsRef = useRef([]);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    const api = {
      setProps: (next) => {
        commandRef.current = next.command;
        const nextItems = next.items || [];
        setItems(nextItems);
        itemsRef.current = nextItems;
        setIndex(0);
        indexRef.current = 0;
        if (next.clientRect) {
          const rect = next.clientRect();
          if (rect) setPos({ left: rect.left, top: rect.bottom + 6 });
        }
        setOpen(true);
      },
      onKeyDown: ({ event }) => {
        const list = itemsRef.current;
        if (!list.length) return false;
        if (event.key === 'ArrowDown') {
          const next = (indexRef.current + 1) % list.length;
          setIndex(next);
          indexRef.current = next;
          return true;
        }
        if (event.key === 'ArrowUp') {
          const next = (indexRef.current - 1 + list.length) % list.length;
          setIndex(next);
          indexRef.current = next;
          return true;
        }
        if (event.key === 'Enter') {
          const item = list[indexRef.current];
          if (item) commandRef.current?.(item);
          return true;
        }
        if (event.key === 'Escape') {
          setOpen(false);
          return true;
        }
        return false;
      },
      close: () => setOpen(false),
    };
    apiRef.current = api;
  }, [apiRef]);

  if (!open) return null;

  // Group by section preserving order.
  const sections = [];
  const seen = new Map();
  items.forEach((it, i) => {
    const key = it.section || 'commands';
    if (!seen.has(key)) {
      seen.set(key, sections.length);
      sections.push({ title: key, items: [] });
    }
    sections[seen.get(key)].items.push({ ...it, _idx: i });
  });

  return createPortal(
    <div className={styles.menu} style={{ left: pos.left, top: pos.top }} role="menu">
      {items.length === 0 && <div className={styles.empty}>No matches</div>}
      {sections.map((s) => (
        <div key={s.title}>
          <div className={styles.section}>{s.title}</div>
          {s.items.map((it) => {
            const active = it._idx === index;
            return (
              <div
                key={it.label}
                className={`${styles.item} ${active ? styles.itemActive : ''}`}
                onMouseEnter={() => setIndex(it._idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commandRef.current?.(it);
                }}
                role="menuitem"
              >
                <span className={styles.itemLabel}>{it.label}</span>
                {it.hint && <span className={styles.itemHint}>{it.hint}</span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>,
    document.body,
  );
}
